import { Button, Grid, Paper, TextField, Typography } from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Dispatch, SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { ConfirmCancellationDialog } from "common/components";
import { useTranslation } from "common/locales";
import { RootState } from "model";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { StyledCoverAppBar } from ".";

const StyledPaper = styled(Paper)`
  margin: auto;
  max-width: 400px;
  padding: 50px;
`;

interface Props {
  dateTimeMeasurementStart: Date;
  setDateTimeMeasurementStart: Dispatch<SetStateAction<Date>>;
  dateTimeMeasurementEnd: Date;
  setDateTimeMeasurementEnd: Dispatch<SetStateAction<Date>>;
}

export default function EnterTime({
  dateTimeMeasurementStart,
  setDateTimeMeasurementStart,
  dateTimeMeasurementEnd,
  setDateTimeMeasurementEnd,
}: Props) {
  const [finishTimeError, setFinishTimeError] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();

  const handleDateChange = (d: Date | null) => {
    if (d) {
      setDateTimeMeasurementStart(d);
      setDateTimeMeasurementEnd(d);
    }
  };
  const handleStartTimeChange = (d: Date | null) => {
    // we want to allow a start time to be changed any time, but set the error if the finish time is before start time
    if (d) {
      setDateTimeMeasurementStart(d);
      if (d < dateTimeMeasurementEnd) {
        setFinishTimeError(false);
      } else {
        setFinishTimeError(true);
      }
    }
  };
  const handleFinishTimeChange = (d: Date | null) => {
    if (d && d > dateTimeMeasurementStart) {
      setFinishTimeError(false);
      setDateTimeMeasurementEnd(d);
    } else {
      setFinishTimeError(true);
    }
  };
  const currentFarmId = useSelector(
    (state: RootState) => state.farms.currentFarmId
  );
  const { formatMessage: f } = useIntl();

  const handleCancel = () => {
    setConfirmCancelOpen(true);
    history.goBack();
  };

  const handleConfirmationClose = () => {
    setConfirmCancelOpen(false);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ marginBottom: { xs: "100px", sm: 0 } }}>
        <Grid item xs={12} sm={3}>
          <Typography variant="h5">
            {t("addCovers.enterTime.heading")}
          </Typography>
          <Typography variant="h6">
            {t("addCovers.enterTime.subheading")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StyledPaper>
              <DatePicker
                disableFuture
                inputFormat="MMMM do"
                label={t("addCovers.enterTime.dateLabel")}
                value={dateTimeMeasurementStart}
                renderInput={(params) => (
                  <TextField variant="standard" {...params} fullWidth />
                )}
                onChange={handleDateChange}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
              <TimePicker
                ampm={false}
                label={t("addCovers.enterTime.startTimeLabel")}
                value={dateTimeMeasurementStart}
                views={["hours", "minutes"]}
                onChange={handleStartTimeChange}
                renderInput={(params) => (
                  <TextField variant="standard" {...params} fullWidth />
                )}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
              <TimePicker
                renderInput={(params) => (
                  <TextField
                    variant="standard"
                    {...params}
                    fullWidth
                    error={finishTimeError}
                    helperText={
                      finishTimeError
                        ? t("addCovers.enterTime.invalidFinishTime")
                        : ""
                    }
                  />
                )}
                ampm={false}
                label={t("addCovers.enterTime.endTimeLabel")}
                value={dateTimeMeasurementEnd}
                views={["hours", "minutes"]}
                onChange={handleFinishTimeChange}
                DialogProps={{
                  /*https://github.com/mui/material-ui/issues/26664*/
                  PaperProps: {
                    sx: { "& *:focus": { outline: "none" } },
                  },
                }}
              />
            </StyledPaper>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={3}></Grid>
      </Grid>
      <StyledCoverAppBar>
        <Button color="secondary" onClick={handleCancel}>
          {f({ id: "addCovers.cancel.label" })}
        </Button>
        <Button
          onClick={() => {
            history.push(`/add-covers/enter-covers`);
          }}
          color="primary"
          disabled={finishTimeError}
        >
          {f({ id: "addCovers.next.label" })}
        </Button>
        <ConfirmCancellationDialog
          open={confirmCancelOpen}
          onClose={handleConfirmationClose}
        />
      </StyledCoverAppBar>
    </>
  );
}
